import { type TextInputSize } from "@/shared/foundation/primitives";

import type { FilterDefinition } from "./FilterDefinition";
import {
  type Filter,
  identityOperator,
  isListComparator,
} from "./FilterSchema";
import { listMemberLabels } from "./operators";
import { BooleanValueInput } from "./valueInputs/BooleanValueInput";
import { DateValueInput } from "./valueInputs/DateValueInput";
import { NumericValueInput } from "./valueInputs/NumericValueInput";
import { OptionsValueInput } from "./valueInputs/OptionsValueInput";
import { StringValueInput } from "./valueInputs/StringValueInput";
import { SuggestionsValueInput } from "./valueInputs/SuggestionsValueInput";
import { TagsValueInput } from "./valueInputs/TagsValueInput";

interface FilterValueInputProps {
  filter: Filter;
  /** The definition `filter` was built from; supplies options for sets. */
  definition: FilterDefinition;
  /**
   * Called with the filter's next values. A list's change that also adds or
   * removes "Not set" (Clear all, Select all) passes its next `includeNull`
   * in the same call, not through `onIncludeNullChange`: a parent that
   * rebuilds its filter from each call would build the second from the
   * filter before the first, undoing it.
   */
  onChange: (next: Filter["values"], includeNull?: boolean) => void;
  /**
   * Called when the author adds or removes the "Not set" member of an
   * "is any of" / "is none of" filter (`includeNull`), and nothing else.
   * Without it, "Not set" is shown when included but never offered.
   */
  onIncludeNullChange?: (includeNull: boolean) => void;
  /** Take focus on mount; off for an editor showing several at once. */
  autoFocus?: boolean;
  /** Marks the input invalid, for an error shown beside it. */
  invalid?: boolean;
  /**
   * The inputs' size, on the `TextInput` scale, to match the fields beside
   * it. Date pickers keep their own. @default "md"
   */
  size?: TextInputSize;
  /**
   * Called with whether a list of typed values (strings, numbers) holds typed
   * text that could be added as a value. It is added when focus leaves the
   * input, so a form can count it as a value (see `FilterForm`).
   */
  onDraftValidChange?: (valid: boolean) => void;
}

/**
 * The value input a filter's type and comparator call for. The filter menu
 * renders it in its popover; an inline editor such as a trigger's condition
 * cards renders the same one.
 *
 * A list comparator (`ANY_OF` / `NONE_OF`) always gets a picker that lists
 * every value, selected ones first, plus the "Not set" member on a nullable
 * field: the field's suggestions plus whatever is typed for typed values
 * (strings, numbers), a checklist for closed sets (enums, booleans). Each
 * member reads after the comparator it stands for, as a pill: "equals
 * nightly", and "is not set" for "Not set" (see `listMemberLabels`).
 */
export function FilterValueInput({
  filter,
  definition,
  onChange,
  onIncludeNullChange,
  autoFocus = true,
  invalid = false,
  size = "md",
  onDraftValidChange,
}: FilterValueInputProps) {
  return renderValueInput(
    filter,
    definition,
    onChange,
    onIncludeNullChange,
    onDraftValidChange,
    { autoFocus, invalid, size },
  );
}

function renderValueInput(
  filter: Filter,
  selectedField: FilterDefinition,
  onChange: (next: Filter["values"], includeNull?: boolean) => void,
  onIncludeNullChange: ((includeNull: boolean) => void) | undefined,
  onDraftValidChange: ((valid: boolean) => void) | undefined,
  inputOptions: { autoFocus: boolean; invalid: boolean; size: TextInputSize },
) {
  const nullable = selectedField.nullable ?? false;
  switch (filter.type) {
    case "string":
      if (isListComparator(filter.comparator)) {
        return (
          <SuggestionsValueInput
            // A new field is a new suggestion source; remounting drops the
            // old source's rows along with the text typed against them.
            key={selectedField.field}
            value={filter.values}
            onChange={onChange}
            memberLabels={listMemberLabels("string")}
            suggestions={
              selectedField.type === "string"
                ? selectedField.suggestions
                : undefined
            }
            nullable={nullable}
            includeNull={filter.includeNull ?? false}
            onIncludeNullChange={onIncludeNullChange}
            onDraftValidChange={onDraftValidChange}
            {...inputOptions}
          />
        );
      }
      return (
        <StringValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          {...inputOptions}
        />
      );
    // `metric` shares the numeric value input. FilterForm isn't used to build
    // metric filters today (the metrics pane is bespoke), but the case keeps
    // the switch exhaustive over `Filter["type"]`.
    case "numeric":
    case "metric":
      return (
        <NumericValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          nullable={nullable}
          includeNull={filter.includeNull ?? false}
          onIncludeNullChange={onIncludeNullChange}
          onDraftValidChange={onDraftValidChange}
          {...inputOptions}
        />
      );
    case "date":
      return (
        <DateValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          invalid={inputOptions.invalid}
        />
      );
    case "boolean":
      return (
        <BooleanValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          size={inputOptions.size}
          nullable={nullable}
          includeNull={filter.includeNull ?? false}
          onIncludeNullChange={onIncludeNullChange}
        />
      );
    case "set":
    case "enum": {
      // A `set` field that lets the author name values its source doesn't
      // list is a tag field, not a picker: the typed text is a first-class
      // value there, so it gets the control built around that.
      if (selectedField.type === "set" && selectedField.allowCustomValue) {
        return (
          <TagsValueInput
            // A new field is a new suggestion source, and the tag field's
            // fetch is keyed on its draft alone; remounting drops the old
            // source's rows along with the draft typed against them.
            key={selectedField.field}
            comparator={filter.comparator}
            value={filter.values}
            onChange={onChange}
            options={selectedField.options}
            size={inputOptions.size}
            invalid={inputOptions.invalid}
          />
        );
      }
      // A set's CONTAINS / NOT_CONTAINS is "has any of these tags", and an
      // enum is always a checklist ("is any of" / "is none of" are its
      // lists), so both pick several. Only an enum list can hold "Not set".
      const isEnumList =
        filter.type === "enum" && isListComparator(filter.comparator);
      const includeNull = filter.type === "enum" && filter.includeNull === true;
      const offersNotSet =
        isEnumList && nullable && onIncludeNullChange !== undefined;
      return (
        <OptionsValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          notSet={
            // An included "Not set" stays listed, ticked, so it can be
            // unticked even where it would no longer be offered.
            offersNotSet || (isEnumList && includeNull)
              ? {
                  selected: includeNull,
                  onToggle: () => onIncludeNullChange?.(!includeNull),
                }
              : undefined
          }
          // Only an enum list's members stand for a comparator of their own: a
          // set's CONTAINS already names its tags' one.
          memberLabels={isEnumList ? listMemberLabels("enum") : undefined}
          size={inputOptions.size}
          invalid={inputOptions.invalid}
          // `filter.type` is seeded from the definition and the form remounts
          // per field, so a set/enum filter always has a set/enum definition.
          // The `[]` fallback is unreachable; it only narrows the type.
          // (`identity` also carries `options`, of a different shape — hence the
          // type check rather than an `in` check.)
          options={
            selectedField.type === "set" || selectedField.type === "enum"
              ? selectedField.options
              : []
          }
          // One option source per field. The form already remounts when the
          // selected field changes, so this is belt-and-braces — but the type
          // requires a key whenever `options` may be a resolver, and the field
          // is the honest answer.
          optionsKey={selectedField.field}
        />
      );
    }
    case "identity": {
      // `filter.type` mirrors `selectedField.type` (both seeded from the same
      // definition), so this guard is unreachable — it narrows `selectedField`
      // to the identity variant for `options`.
      if (selectedField.type !== "identity") {
        return null;
      }
      // The operator names the principal type, and the type names its
      // directory. Both lookups are total for a comparator that came from
      // `effectiveComparators(selectedField)`, which only emits operators for
      // principal types this definition declares.
      const operator = identityOperator(filter.comparator);
      const typeOptions = selectedField.options.find(
        (option) => option.type === operator?.type,
      );
      if (typeOptions === undefined) {
        return null;
      }
      return (
        <OptionsValueInput
          // Switching operator moves to a different directory while staying on
          // one field, so `FilterForm` itself never remounts (it is keyed by
          // field). Keying the picker by principal type instead drops the
          // search text, the latched option set and the pinned rows together —
          // all of which were typed against, or fetched from, the old kind.
          key={typeOptions.type}
          // The principal type lives on the filter's comparator; within one
          // type, picking entities is plain multi-select equality, sent as an
          // OR of `EQUALS` when several are picked.
          comparator="ANY_OF"
          value={filter.values}
          onChange={onChange}
          size={inputOptions.size}
          invalid={inputOptions.invalid}
          options={typeOptions.optionResolver}
          // Keeps the two kinds' query entries distinct across that remount.
          optionsKey={typeOptions.type}
        />
      );
    }
  }
}
